import React, { useState, useContext, useMemo, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { gql, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import { usePermissions } from "../../hooks/usePermissions";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityOperationBar from "../../components/EntityOperationBar";
import "../../starek.css";
import DataTable from "react-data-table-component";
import moment from "moment";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import WebhookSidebar, { DELETE_ONE_WEBHOOK } from "./WebhookSidebar";
import WebhooksCardView, { WebhooksEmptyState } from "./WebhooksCardView";
import { buildListRoute } from "../../utils";
import usePagedList, {
    nameDescriptionSearch,
    sortToOrderBy,
} from "../../hooks/usePagedList";
import Paginator from "../../components/Paginator";

// One page of webhooks plus the total, for server-side pagination (usePagedList).
// The sidebar reads the full webhook by id itself (GET_WEBHOOK), so a deep link to a
// webhook on another page needs no separate by-id query here.
const GET_WEBHOOKS_LIST = gql`
    query GetWebhooks(
        $where: WebhookWhereInput!
        $orderBy: [WebhookOrderByWithRelationInput!]
        $take: Int
        $skip: Int
    ) {
        webhooks(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
            id
            name
            description
            url
            method
            parameters
            is_active
            created_at
        }
        aggregateWebhook(where: $where) {
            _count {
                _all
            }
        }
    }
`;

// Search has always matched the URL too, not just name / description.
const webhookSearch = (text) => {
    const base = nameDescriptionSearch(text);
    if (!base) return null;
    return { OR: [...base.OR, { url: { contains: text.trim() } }] };
};

const Webhooks = () => {
    const starekContext = useContext(StarekContext);
    const { can } = usePermissions();
    const canWrite = can("webhook", "WRITE");
    const canDestroy = can("webhook", "DESTROY");
    const navigate = useNavigate();
    const { id: urlId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState("card");

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam === "card" || viewParam === "list") setViewMode(viewParam);
    }, [searchParams]);

    const [deleteOneWebhook] = useMutation(DELETE_ONE_WEBHOOK, {
        client: starekContext.engineClient,
    });

    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [toggleCleared, setToggleCleared] = useState(false);

    const [showSidebar, setShowSidebar] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Table sort, applied on the server: { field, direction } or null for default order.
    const [sort, setSort] = useState(null);
    const orderBy = useMemo(() => sortToOrderBy(sort, ["description"]), [sort]);

    const webhookQueryWhere = useMemo(
        () => ({ plant_id: { equals: starekContext.lastUsedPlant.id } }),
        [starekContext.lastUsedPlant.id],
    );

    // One page (12) of webhooks from the server, searched on name/description/url.
    const {
        items: webhooks,
        total,
        page,
        pageCount,
        pageSize,
        setPage,
        loading,
        initialLoading,
        error,
        refetch,
    } = usePagedList({
        query: GET_WEBHOOKS_LIST,
        listField: "webhooks",
        countField: "aggregateWebhook",
        where: webhookQueryWhere,
        searchText: filterText,
        buildSearch: webhookSearch,
        orderBy,
        client: starekContext.engineClient,
    });

    // Selection is per page: moving pages clears it.
    useEffect(() => {
        setSelectedRows([]);
        setToggleCleared((p) => !p);
    }, [page]);

    // Open sidebar when navigating to /webhooks/:id. The webhook may be on another page
    // (or none, under a search); the sidebar loads it by id, so `{ id }` is enough.
    useEffect(() => {
        if (urlId != null) {
            const id = Number(urlId);
            if (id === 0) {
                setEditingItem(null);
                setShowSidebar(true);
            } else if (!initialLoading) {
                const found = webhooks.find((w) => w.id === id);
                setEditingItem(found ?? { id });
                setShowSidebar(true);
            }
        }
    }, [urlId, initialLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    // Routes keep the list's query string (?page, ?view) so the list behind the sidebar
    // stays on the same page.
    const openAdd = () => {
        setEditingItem(null);
        setShowSidebar(true);
        navigate(buildListRoute("/webhooks/0", searchParams));
    };

    const openEdit = () => {
        if (selectedRows.length !== 1) return;
        setEditingItem(selectedRows[0]);
        setShowSidebar(true);
        navigate(buildListRoute(`/webhooks/${selectedRows[0].id}`, searchParams));
    };

    const closeSidebar = () => {
        setShowSidebar(false);
        setEditingItem(null);
        navigate(buildListRoute("/webhooks", searchParams));
    };

    const handleSave = async () => {
        await refetch();
        closeSidebar();
    };

    const handleDeleteFromSidebar = async (item) => {
        await deleteOneWebhook({ variables: { where: { id: item.id } } });
        await refetch();
        closeSidebar();
    };

    const handleBulkDelete = async () => {
        setIsDeleting(true);
        setDeleteDone(0);
        try {
            for (const webhook of selectedRows) {
                await deleteOneWebhook({ variables: { where: { id: webhook.id } } });
                setDeleteDone((n) => n + 1);
            }
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await refetch();
        } catch (err) {
            starekContext.showError(err?.message || err);
        } finally {
            setIsDeleting(false);
            setDeleteDone(0);
        }
    };

    const columns = [
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            sortField: "name",
            cell: (row) => (
                <div
                    className="d-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                        setEditingItem(row);
                        setShowSidebar(true);
                        navigate(buildListRoute(`/webhooks/${row.id}`, searchParams));
                    }}
                >
                    <span className="w-30px h-30px me-2 d-flex align-items-center justify-content-center rounded-circle border bg-color-f2f2f2">
                        <i className="fa fa-link" />
                    </span>
                    <span>{row.name}</span>
                </div>
            ),
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
            sortField: "description",
        },
        {
            name: "Method",
            selector: (row) => row.method,
            sortable: true,
            sortField: "method",
            width: "100px",
        },
        {
            name: "Type",
            selector: (row) => row.parameters?.api_type ?? "REST",
            // Derived from the `parameters` Json column, which the server cannot sort by.
            sortable: false,
            width: "110px",
            cell: (row) => {
                const t = row.parameters?.api_type ?? "REST";
                return t === "GRAPHQL"
                    ? <span className="badge" style={{ backgroundColor: "#e10098", color: "#fff" }}>GraphQL</span>
                    : <span className="badge bg-info text-white">REST</span>;
            },
        },
        {
            name: "URL",
            selector: (row) => row.url,
            sortable: true,
            sortField: "url",
            cell: (row) => (
                <span
                    title={row.url}
                    style={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "inline-block",
                    }}
                >
                    {row.url}
                </span>
            ),
        },
        {
            name: "Active",
            selector: (row) => row.is_active,
            sortable: true,
            sortField: "is_active",
            width: "90px",
            cell: (row) => (
                <span className={`badge bg-${row.is_active ? "success" : "secondary"}`}>
                    {row.is_active ? "Yes" : "No"}
                </span>
            ),
        },
        {
            name: "Created",
            selector: (row) => moment.utc(row.created_at).format("YYYY-MM-DD HH:mm"),
            sortable: true,
            sortField: "created_at",
            width: "160px",
        },
    ];

    const customStyles = { headCells: { style: { fontWeight: "bold" } } };

    // Only the first load gets the full-page spinner; a page change or a refetch after a
    // write keeps the list (and an open sidebar) on screen.
    if (initialLoading) return <EntityLoadingState icon="fa-link" />;

    // Full-page error only when nothing could be shown; a failed page change, search
    // or refetch keeps the last rows and shows an inline alert instead.
    if (error && webhooks.length === 0) {
        return (
            <div className="tw-text-center tw-mt-10 tw-text-red-600">
                Failed to load webhooks: {error.message}
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">Webhooks</div>
            {error && (
                <div className="alert alert-danger py-2 small">
                    Could not refresh webhooks: {error.message}
                </div>
            )}
            <EntityOperationBar
                entity={{ singular: "Webhook", plural: "Webhooks" }}
                allowSearch
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onAdd={openAdd}
                onEdit={openEdit}
                onDelete={handleBulkDelete}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    // Keep ?page (and anything else) — both views show the same page.
                    setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.set("view", mode);
                        return next;
                    });
                }}
            >
                <EntityOperationBar.View mode="card" icon="fa-th" tooltip="Card View">
                    <WebhooksCardView
                        items={webhooks}
                        canAdd={canWrite}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                        filterText={filterText}
                    />
                </EntityOperationBar.View>
                <EntityOperationBar.View mode="list" icon="fa-list-ul" tooltip="List View">
                    <DataTable
                        columns={columns}
                        data={webhooks}
                        selectableRows
                        onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                        clearSelectedRows={toggleCleared}
                        // `webhooks` is the current page, searched and sorted on the
                        // server; the Paginator below handles paging.
                        sortServer
                        onSort={(column, direction) =>
                            setSort(column.sortField ? { field: column.sortField, direction } : null)
                        }
                        highlightOnHover
                        striped
                        responsive
                        customStyles={customStyles}
                        noDataComponent={<WebhooksEmptyState isFiltered={!!filterText.trim()} canAdd={canWrite} />}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <Paginator
                page={page}
                pageCount={pageCount}
                pageSize={pageSize}
                total={total}
                onChange={setPage}
                disabled={loading}
            />

            <WebhookSidebar
                show={showSidebar}
                item={editingItem}
                onSave={handleSave}
                onDelete={handleDeleteFromSidebar}
                onDiscard={closeSidebar}
            />
        </div>
    );
};

export { Webhooks };
